import { Injectable, SecurityContext, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { DomSanitizer, Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, NavigationStart } from '@angular/router';

import { NoteService } from './note_service';
import { StreamService } from './stream_service';
import { UserService } from './user_service';
import { EnvironmentService } from './environment_service';

import { Note } from '../types/note';
import { User } from '../types/user';

@Injectable({
  providedIn: 'root'
})
export class MetaService {
  private platformId = inject(PLATFORM_ID);
  private meta = inject(Meta);
  private pageTitle = inject(Title);
  private route = inject(ActivatedRoute);
  private noteService = inject(NoteService);
  private streamService = inject(StreamService);
  private userService = inject(UserService);
  private envService = inject(EnvironmentService);
  private domSanitizer = inject(DomSanitizer);
  private router = inject(Router);
  private document: Document = inject(DOCUMENT);

  keywords: MetaDefinition;
  description: MetaDefinition;
  defaultMetaTags: any;
  title: string;
  notdLogo: string;
  notdLogoSquare: string;

  constructor() {
    this.defaultMetaTags = {
      title: 'Notd.io',
      description: 'Notd is a media subscription network for individuals and publishers.',
      keywords: 'Notd.io'
    };

    this.keywords = {
      name: 'keywords',
      content: 'Notd.io'
    };

    this.setDefaultTags();

    this.router.events
      .subscribe(evt => {
        if (evt instanceof NavigationStart) {
          this.clearSpecialTags();
          this.setMetaPropertyTag('og:url', `${this.envService.urlBasePrefix}${evt.url}`);
          this.setMetaPropertyTag('twitter:url', `${this.envService.urlBasePrefix}${evt.url}`);
          this.setMetaPropertyTag('og:site_name', this.defaultMetaTags.title);
          this.setMetaPropertyTag('og:type', 'website');
          this.setMetaPropertyTag('og:image', this.notdLogo);
          this.setMetaPropertyTag('twitter:image', this.notdLogoSquare);

          if (
            evt.url === '/' ||
            evt.url.startsWith('/info')
          ) {
            this.setTitle(this.defaultMetaTags.title);
            this.setDescription(this.defaultMetaTags.description);
          } else if (evt.url.indexOf('notes/') >= 0 || evt.url.indexOf('/n/') === 0) {
            this.getIdFromUrl(evt.url, 'notes').then((id: string | number | null) => {
              if (id) {
                this.setNoteTags(id.toString());
              } else {
                this.setDefaultTags();
                this.setTitle(this.defaultMetaTags.title);
                this.setDescription(this.defaultMetaTags.description);
                this.setKeywords(this.defaultMetaTags.keywords);
              }
            });
          } else if (evt.url.indexOf('thread/') >= 0) {
            this.getIdFromUrl(evt.url, 'thread').then((id: string | number | null) => {
              if (id) {
                this.setNoteTags(id.toString());
              } else {
                this.setDefaultTags();
                this.setTitle(this.defaultMetaTags.title);
                this.setDescription(this.defaultMetaTags.description);
                this.setKeywords(this.defaultMetaTags.keywords);
              }
            });
          } else if (evt.url.indexOf('streams/') >= 0 && evt.url.indexOf('user/') < 0) {
            this.getIdFromUrl(evt.url, 'streams').then((id: string | number | null) => {
              if (id) {
                this.setStreamTags(id.toString());
              } else {
                this.setDefaultTags();
                this.setTitle(this.defaultMetaTags.title);
                this.setDescription(this.defaultMetaTags.description);
                this.setKeywords(this.defaultMetaTags.keywords);
              }
            });
          } else if (evt.url.indexOf('streams/') >= 0 && evt.url.indexOf('user/') >= 0) {
            this.getIdFromUrl(evt.url, 'streams-user').then((id: string | number | null) => {
              if (id) {
                this.setStreamTags(id.toString());
              } else {
                this.setDefaultTags();
                this.setTitle(this.defaultMetaTags.title);
                this.setDescription(this.defaultMetaTags.description);
                this.setKeywords(this.defaultMetaTags.keywords);
              }
            });
          } else if (evt.url.indexOf('/s/') === 0) {
            this.getIdFromUrl(evt.url, 'streams').then((id: string | number | null) => {
              if (id) {
                this.setStreamTags(id.toString());
              } else {
                this.setDefaultTags();
                this.setTitle(this.defaultMetaTags.title);
                this.setDescription(this.defaultMetaTags.description);
                this.setKeywords(this.defaultMetaTags.keywords);
              }
            });
          } else if (evt.url.indexOf('/user/') === 0) {
            const urlSegments = evt.url.split('/');
            let foundUserId: string | null = null;

            for (let i = 1; i < urlSegments.length; i++) {
              const segment = urlSegments[i];
              if (segment && /^\d+$/.test(segment) && segment.length > 10) {
                foundUserId = segment;
                break;
              }
            }

            if (foundUserId) {
              this.setUserTags(foundUserId);
            } else {
              this.setDefaultTags();
              this.setTitle(this.defaultMetaTags.title);
              this.setDescription(this.defaultMetaTags.description);
              this.setKeywords(this.defaultMetaTags.keywords);
            }
          } else if (evt.url.indexOf('/search') >= 0) {
            this.setTitle(`Search - ${this.defaultMetaTags.title}`);
            this.setMetaPropertyTag('og:type', 'website');
          } else if (evt.url.indexOf('/promotion') >= 0) {
            this.setTitle('$100 Promotion');
            this.setDescription(
              'Like to Write? Get $100 for Trying Notd. '
              + 'We just launched Notd, where writers and publishers '
              + 'can offer micro-subscriptions to their news, opinions, '
              + 'commentary – whatever they write. And to show what a '
              + 'great way it is to publish (and read), we are offering '
              + 'writers $100 to try it out. ');
          } else if (evt.url.indexOf('/why-publish') >= 0) {
            this.setTitle('Notd.io: The Publishing Network You’ve Been Waiting For.');
            this.setDescription(
              'The Publishing Network You’ve Been Waiting For. '
              + 'You’re a writer. Maybe you already have a following, '
              + 'or maybe you’re just starting out. You need a '
              + 'publishing platform, but most of them exist primarily '
              + 'to make money from your work. ');
            const imageUrl = `${this.envService.urlBasePrefix}/assets/why-publish/reading-man.png`;
            this.setMetaPropertyTag('og:image', imageUrl);
            this.setMetaPropertyTag('twitter:image', imageUrl);
          } else if (evt.url.indexOf('/how-notd-works') === 0) {
            this.setTitle('Notd.io: A Fully Customizable, Ad-Free News and Information Network ');
            this.setDescription(
              'Notd is a revolutionary publishing network: '
              + 'a go-to source for unbiased news, podcasts, '
              + 'newsletters, poetry, and more.');
            const imageUrl = `${this.envService.urlBasePrefix}/assets/why-publish/reading-man.png`;
            this.setMetaPropertyTag('og:image', imageUrl);
            this.setMetaPropertyTag('twitter:image', imageUrl);
          } else if (evt.url.indexOf('/sitemap') === 0) {
            this.setTitle('Notd.io: Sitemap');
            this.setDescription(
              'Notd sitemap');
          } else {
            this.setDefaultTags();
            this.setTitle(this.defaultMetaTags.title);
            this.setKeywords(this.defaultMetaTags.keywords);
            this.setDescription(this.defaultMetaTags.description);
          }
        }
      });
  }

  setDefaultTags() {
    const prevDescriptionTags = this.meta.getTags('name="description"');

    if (prevDescriptionTags.length <= 0) {
      this.notdLogo = `${this.envService.urlBasePrefix}/assets/favicon.png`;
      this.notdLogoSquare = `${this.envService.urlBasePrefix}/assets/favicon.png`;
      this.title = 'Notd.io';
      this.description = {
        name: 'description',
        content: this.defaultMetaTags.description,
      };
      this.keywords = {
        name: 'keywords',
        content: this.defaultMetaTags.keywords
      };
      this.setTitle(this.title);
      this.setKeywords(this.defaultMetaTags.keywords);
      this.setDescription(this.defaultMetaTags.description);
    }
  }

  setTitle(title: string) {
    this.title = title;
    this.pageTitle.setTitle(this.title);
    this.setMetaPropertyTag('og:title', this.title);
    this.setMetaPropertyTag('twitter:title', this.title);
  }

  setKeywords(keywords: string) {
    this.keywords.content = keywords;
    if (this.meta.getTag('name="keywords"')) {
      this.meta.updateTag(this.keywords);
    } else {
      this.meta.addTag(this.keywords);
    }
  }

  setDescription(description: string | null): void {
    if (!description || description.length < 2) {
      return;
    }
    description = this.domSanitizer.sanitize(SecurityContext.NONE, description) || '';
    description = description.replace(/\n/g, '').replace(/<.*?>/g, '').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    if (this.description && ('content' in this.description) && description) {
      this.description.content = description;
      this.meta.updateTag(this.description);
      this.setMetaPropertyTag('og:description', description);
      this.setMetaPropertyTag('twitter:description', description);
    }
  }

  setMetaPropertyTag(tag: string, content: string) {
    const metaDef: MetaDefinition = { property: tag, content };
    if (this.meta.getTag('property="' + tag + '"')) {
      this.meta.updateTag(metaDef);
    } else {
      this.meta.addTag(metaDef);
    }
  }

  setNoteTags(noteId: string) {
    this.noteService.getNoteInfo(noteId).then((res: Note) => {
      const title = `${res.name} - ${this.defaultMetaTags.title}`;
      this.setTitle(title);
      this.setDescription(res['firstLeadWords']?.toString() || res['firstPremWords']?.toString() || '');

      const ldJson = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': this.noteService.getFullURL(res.id, res.name),
        },
        headline: title,
        isAccessibleForFree: 'False',
        image: [] as string[],
        publisher: {
          name: 'Notd.io',
          '@type': 'Organization',
          logo: {
            '@type': 'ImageObject',
            url: this.envService.urlBasePrefix + '/assets/logo-black.png'
          },
          hasPart: {
            '@type': 'WebPageElement',
            isAccessibleForFree: 'False',
            cssSelector: '.paywall'
          }
        },
        datePublished: (new Date(res.published)).toISOString(),
        dateModified: (new Date(res.updated)).toISOString()
      };

      if (res.thumbnailImage) {
        this.setMetaPropertyTag('og:image', res.thumbnailImage);
        this.setMetaPropertyTag('twitter:image', res.thumbnailImage);
        ldJson.image.push(res.thumbnailImage);
      } else {
        if (res.stream && res.stream.iconImageUrl) {
          this.setMetaPropertyTag('og:image', res.stream.iconImageUrl);
          this.setMetaPropertyTag('twitter:image', res.stream.iconImageUrl);
          ldJson.image.push(res.stream.iconImageUrl);
        }
      }
      this.setLdJSON(ldJson);
    }, (err) => {
      console.log('Could not get note info! Setting default tags', err);
      this.setDefaultTags();
      this.setTitle(this.defaultMetaTags.title);
      this.setDescription(this.defaultMetaTags.description);
      this.setKeywords(this.defaultMetaTags.keywords);
    });
  }

  setUserTags(userNameOrId: string) {
    this.userService.getUserProfile(userNameOrId).then((user: User | null) => {
      if (!user) {
        this.setDefaultTags();
        return;
      }
      if (user.profileImageUrl) {
        this.setMetaPropertyTag('og:image', user.profileImageUrl);
        this.setMetaPropertyTag('twitter:image', user.profileImageUrl);
      }
      if (user.bio) {
        this.setDescription(user.bio);
      }
      if (user.displayName) {
        this.setTitle(`${user.displayName} - ${this.defaultMetaTags.title}`);
        this.setKeywords(`Notd.io writer author ${user.displayName}`);
      } else {
        this.setTitle(this.defaultMetaTags.title);
      }
    });
  }

  setStreamTags(streamId: string) {
    this.streamService.getStreamInfoObservable(streamId).subscribe(res => {
      if (res) {
        this.setTitle(`${res.name} - ${this.defaultMetaTags.title}`);
        this.setMetaPropertyTag('og:type', 'website');
        this.setDescription(res.description);
        if (res.iconImageUrl) {
          this.setMetaPropertyTag('og:image', res.iconImageUrl);
          this.setMetaPropertyTag('twitter:image', res.iconImageUrl);
        }
      } else {
        this.setDefaultTags();
      }
    }, (err) => {
      console.log('Could not get stream info! Setting default tags', err);
      this.setDefaultTags();
    });
  }

  private clearSpecialTags() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.meta.removeTag('property="og:type"');
    this.meta.removeTag('property="og:image"');
    this.meta.removeTag('property="twitter:image"');
    const oldLdJsonScript = this.document.head.querySelector('script[type="application/ld+json"]');
    if (oldLdJsonScript) {
      this.document.head.removeChild(oldLdJsonScript);
    }
  }

  private setLdJSON(ldJson: any) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(ldJson);
    this.document.head.appendChild(script);
  }

  private getIdFromUrl(url: string, type: string): Promise<number | string | null> {
    const urlSplit = url.split('/');
    const isNumericId = (val: string) => val && /^\d+$/.test(val);
    const isReservedWord = (val: string) => ['create', 'edit', 'settings', 'publish-thank-you', 'profile'].includes(val);

    if (url.indexOf('/s/') >= 0) {
      const customUrlString = urlSplit[2];
      if (isReservedWord(customUrlString)) return Promise.resolve(null);
      return this.streamService.getStreamIdFromCustomUrl(customUrlString)
        .then(result => result?.channel?.id || null)
        .catch(err => {
          console.error('Error while getting stream by custom URL', err);
          return null;
        });
    } else if (url.indexOf('/n/') >= 0) {
      const customUrlString = urlSplit[2];
      if (isReservedWord(customUrlString)) return Promise.resolve(null);
      return this.noteService.getNoteIdFromCustomUrl(customUrlString)
        .then(result => result?.post?.id || null)
        .catch(err => {
          console.error('Error while getting note by custom URL', err);
          return null;
        });
    } else if (url.indexOf('/notes/publish-thank-you') >= 0) {
      const customUrlString = urlSplit[3];
      return Promise.resolve(isNumericId(customUrlString) ? customUrlString : null);
    } else {
      let potentialId: string | null = null;
      if (type === 'notes') potentialId = urlSplit[2];
      if (type === 'streams') potentialId = urlSplit[2];
      if (type === 'streams-user') potentialId = urlSplit[4];
      if (type === 'user') {
        potentialId = urlSplit.find(segment => /^\d{10,}$/.test(segment)) || urlSplit[2];
      }
      if (type === 'thread') potentialId = urlSplit[2]?.split('?')[0];

      if (potentialId && (isReservedWord(potentialId) || !isNumericId(potentialId))) {
        return Promise.resolve(null);
      }
      return Promise.resolve(potentialId || null);
    }
  }
}